import { faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { getIn, useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { DonationUnderSection80GForm, DonationUnderSection80GGAForm } from ".";
import { RegimeTypes } from "../../../config/filingConstants";
import { IFormITR1, ISection80GDonation, ISection80GGADonation } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { FormField, NumericInputField, SelectInputField } from "../../fields";
import { ConfirmationModal } from "../../modals";

const DeductionsOnDonationsEduHRADetails: React.FC = () => {
	const { values, touched, errors, setFieldValue, handleBlur } = useFormikContext<IFormITR1>();
	const RegimeType = values?.FilingReturnInfo?.RegimeType || RegimeTypes.NEW_TAX_REGIME;

	const [show80GFormCanvas, setShow80GFormCanvas] = useState<boolean>(false);
	const [show80GGAFormCanvas, setShow80GGAFormCanvas] = useState<boolean>(false);
	const [show80GDeleteModal, setShow80GDeleteModal] = useState<ISection80GDonation | null>(null);
	const [show80GGADeleteModal, setShow80GGADeleteModal] = useState<ISection80GGADonation | null>(
		null
	);
	const [active80GItem, setActive80GItem] = useState<ISection80GDonation | null>(null);
	const [active80GGAItem, setActive80GGAItem] = useState<ISection80GGADonation | null>(null);
	// used in both edit and delete cases
	const [activeItemIdx, setActiveItemIdx] = useState<string>("");

	const Section80GDonationDetails = cloneDeep(
		values.DonationsEduHRADeductions?.Section80GDonationDetails || []
	);
	const Section80GGADonationDetails = cloneDeep(
		values.DonationsEduHRADeductions?.Section80GGADonationDetails || []
	);

	const handleDelete80GItem = async () => {
		Section80GDonationDetails.splice(Number(activeItemIdx), 1);
		setFieldValue("DonationsEduHRADeductions.Section80GDonationDetails", Section80GDonationDetails);
		setShow80GDeleteModal(() => null);
		setActive80GItem(() => null);
		setActiveItemIdx(() => "");
		handleTotalCalSection80G();
	};

	const handleDelete80GGAItem = async () => {
		Section80GGADonationDetails.splice(Number(activeItemIdx), 1);
		setFieldValue(
			"DonationsEduHRADeductions.Section80GGADonationDetails",
			Section80GGADonationDetails
		);
		setShow80GGADeleteModal(() => null);
		setActive80GGAItem(() => null);
		setActiveItemIdx(() => "");
		handleTotalCalSection80GGA();
	};

	const handleTotalCalculation = (section: string, amount: number) => {
		const _Total = Object.entries(values.DeductUndChapVIA).reduce((curr, [key, value]) => {
			if (key === section) {
				return Number(curr) + Number(amount);
			}
			return Number(curr) + Number(value);
		}, 0);

		setFieldValue("TotalChapVIADeductions", _Total);
	};

	const handleTotalCalSection80G = () => {
		const TotalSection80G =
			Section80GDonationDetails?.reduce((acc, curr) => {
				const value = curr.TotalDonation;
				return acc + (isNaN(value) ? 0 : value);
			}, 0) || 0;

		setFieldValue("DonationsEduHRADeductions.Section80GDonationDetails", Section80GDonationDetails);
		setFieldValue("DeductUndChapVIA.Section80G", TotalSection80G);
		handleTotalCalculation("Section80G", TotalSection80G);
	};

	const handleTotalCalSection80GGA = () => {
		const TotalSection80GGA =
			Section80GGADonationDetails?.reduce((acc, curr) => {
				const value = curr.TotalDonation;
				return acc + (isNaN(value) ? 0 : value);
			}, 0) || 0;

		setFieldValue(
			"DonationsEduHRADeductions.Section80GGADonationDetails",
			Section80GGADonationDetails
		);
		setFieldValue("DeductUndChapVIA.Section80GGA", TotalSection80GGA);
		handleTotalCalculation("Section80GGA", TotalSection80GGA);
	};

	return (
		<>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80G - Donations to charitable organizations
						<small>The government requires itemized details of donations for Section 80G.</small>
					</h5>
					<PreventEditComponent>
						<Button
							variant="secondary"
							size="sm"
							onClick={() => setShow80GFormCanvas(() => true)}
							disabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
						>
							<FASvgIcon icon={faPlus} />
							Add
						</Button>
					</PreventEditComponent>
				</div>
				<dl className="eds-list eds-dl eds-inline-between">
					{Section80GDonationDetails?.map((donation, index) => (
						<div key={`80g-donation-${index}`} className="eds-list--item">
							<dt>{donation.DoneeName}</dt>
							<dd>
								<span>
									<PriceDisplay price={donation?.TotalDonation || 0} />
								</span>
								<Button
									variant="link"
									onClick={() => {
										setShow80GFormCanvas(() => true);
										setActive80GItem(() => donation);
										setActiveItemIdx(() => String(index));
									}}
								>
									View
									<PreventEditComponent>
										<>/Edit</>
									</PreventEditComponent>
								</Button>
								<Button
									variant="transparent"
									className="icon-only"
									onClick={() => {
										setShow80GDeleteModal(() => donation);
										setActiveItemIdx(() => String(index));
									}}
								>
									<FASvgIcon icon={faTrash} />
								</Button>
							</dd>
						</div>
					))}
				</dl>
			</section>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80GGA - Donations for Scientific Research or Rural Development
						<small>The government requires itemized details of donations for Section 80GGA.</small>
					</h5>
					<PreventEditComponent>
						<Button
							variant="secondary"
							size="sm"
							onClick={() => setShow80GGAFormCanvas(() => true)}
							disabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
						>
							<FASvgIcon icon={faPlus} />
							Add
						</Button>
					</PreventEditComponent>
				</div>
				<dl className="eds-list eds-dl eds-inline-between">
					{Section80GGADonationDetails?.map((donation, index) => (
						<div key={`80gga-donation-${index}`} className="eds-list--item">
							<dt>{donation.DoneeName}</dt>
							<dd>
								<span>
									<PriceDisplay price={donation?.TotalDonation || 0} />
								</span>
								<Button
									variant="link"
									onClick={() => {
										setShow80GGAFormCanvas(() => true);
										setActive80GGAItem(() => donation);
										setActiveItemIdx(() => String(index));
									}}
								>
									View
									<PreventEditComponent>
										<>/Edit</>
									</PreventEditComponent>
								</Button>
								<Button
									variant="transparent"
									className="icon-only"
									onClick={() => {
										setShow80GGADeleteModal(() => donation);
										setActiveItemIdx(() => String(index));
									}}
								>
									<FASvgIcon icon={faTrash} />
								</Button>
							</dd>
						</div>
					))}
				</dl>
			</section>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>80E - Education Loan on higher studies (Graduate or Postgraduate)</h5>
				</div>
				<FormField label="Interest on higher education loan paid this year" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="DonationsEduHRADeductions.TotalSection80E"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.DonationsEduHRADeductions.TotalSection80E
						}
						onBlur={handleBlur("DonationsEduHRADeductions.TotalSection80E")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("DonationsEduHRADeductions.TotalSection80E", value);
							setFieldValue("DeductUndChapVIA.Section80E", Number(value));
							handleTotalCalculation("Section80E", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `DonationsEduHRADeductions.TotalSection80E`)) &&
							getIn(touched, `DonationsEduHRADeductions.TotalSection80E`)
						}
						errorMsg={getIn(errors, `DonationsEduHRADeductions.TotalSection80E`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>
			<section>
				<div className="section-title-wrap">
					<h5>
						80GG - Deduction for House Rent (Self-employed or Salaried with no HRA)
						<small>
							Your spouse/child should not own a property in same town as this property. Deduction
							for interest on owned property should not be availed. One cannot claim both HRA
							exemption and deduction under 80GG
						</small>
					</h5>
				</div>
				<FormField label="Rent paid per month" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="DonationsEduHRADeductions.TotalSection80GG"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.DonationsEduHRADeductions.TotalSection80GG
						}
						onBlur={handleBlur("DonationsEduHRADeductions.TotalSection80GG")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("DonationsEduHRADeductions.TotalSection80GG", value);
							const total =
								Number(values.DonationsEduHRADeductions?.S80GGPaidRentMonths || 1) * Number(value);
							setFieldValue("DeductUndChapVIA.Section80GG", total);
							handleTotalCalculation("Section80GG", total);
						}}
						isInvalid={
							Boolean(getIn(errors, `DonationsEduHRADeductions.TotalSection80GG`)) &&
							getIn(touched, `DonationsEduHRADeductions.TotalSection80GG`)
						}
						errorMsg={getIn(errors, `DonationsEduHRADeductions.TotalSection80GG`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
				<FormField label="Number of Months (1 to 12)" sm={6}>
					<SelectInputField
						placeholder="Please Select Number of months"
						name="DonationsEduHRADeductions.S80GGPaidRentMonths"
						menuZIndex={9}
						isOptionArrayOfObject={false}
						options={["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"]}
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ["1"]
								: [values.DonationsEduHRADeductions?.S80GGPaidRentMonths || "1"]
						}
						onChange={(selectedOption) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}

							if (selectedOption) {
								setFieldValue(
									`DonationsEduHRADeductions.S80GGPaidRentMonths`,
									selectedOption.value
								);
							} else {
								setFieldValue(`DonationsEduHRADeductions.S80GGPaidRentMonths`, undefined);
							}

							const total =
								Number(selectedOption?.value || 1) *
								Number(values.DonationsEduHRADeductions?.TotalSection80GG || 0);
							setFieldValue("DeductUndChapVIA.Section80GG", total);
							handleTotalCalculation("Section80GG", total);
						}}
						onBlur={handleBlur(`DonationsEduHRADeductions.S80GGPaidRentMonths`)}
						isInvalid={
							Boolean(getIn(errors, `DonationsEduHRADeductions.S80GGPaidRentMonths`)) &&
							getIn(touched, `DonationsEduHRADeductions.S80GGPaidRentMonths`)
						}
						errorMsg={getIn(errors, `DonationsEduHRADeductions.S80GGPaidRentMonths`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>

			<DonationUnderSection80GForm
				showCanvas={show80GFormCanvas}
				handleCloseBtnPress={(donationData) => {
					if (donationData) {
						if (active80GItem) {
							Section80GDonationDetails?.splice(Number(activeItemIdx), 1, donationData);
						} else {
							Section80GDonationDetails?.push(donationData);
						}
						handleTotalCalSection80G();
					}
					setShow80GFormCanvas(() => false);
					setActive80GItem(() => null);
					setActiveItemIdx(() => "");
				}}
				formValues={active80GItem}
			/>

			<DonationUnderSection80GGAForm
				showCanvas={show80GGAFormCanvas}
				handleCloseBtnPress={(donationData) => {
					if (donationData) {
						if (active80GGAItem) {
							Section80GGADonationDetails?.splice(Number(activeItemIdx), 1, donationData);
						} else {
							Section80GGADonationDetails?.push(donationData);
						}
						handleTotalCalSection80GGA();
					}
					setShow80GGAFormCanvas(() => false);
					setActive80GGAItem(() => null);
					setActiveItemIdx(() => "");
				}}
				formValues={active80GGAItem}
			/>

			<ConfirmationModal
				showModal={!!show80GDeleteModal}
				iconColorClass="text-danger"
				title="Are you sure?"
				message={<p>You want to delete {show80GDeleteModal?.DoneeName}</p>}
				onYesBtnClick={() => handleDelete80GItem()}
				onNoBtnClick={() => setShow80GDeleteModal(() => null)}
				icon={faTrashAlt}
			/>

			<ConfirmationModal
				showModal={!!show80GGADeleteModal}
				iconColorClass="text-danger"
				title="Are you sure?"
				message={<p>You want to delete {show80GGADeleteModal?.DoneeName}</p>}
				onYesBtnClick={() => handleDelete80GGAItem()}
				onNoBtnClick={() => setShow80GGADeleteModal(() => null)}
				icon={faTrashAlt}
			/>
		</>
	);
};

export default DeductionsOnDonationsEduHRADetails;
